import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  AlertTitle,
  CircularProgress,
  Link,
  Divider,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import TranslateIcon from '@mui/icons-material/Translate';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { fetchSubtitleText, getVideo, messageOf, patchVideo, subtitlePipeline, unwrap } from '../admin/api';
import { SUBTITLE_LABELS, staleFlags, stalePatch, subtitleCoverage } from '../admin/schema';

// One server-side pipeline owns the whole job: it corrects the pasted English
// transcription, translates it, uploads all five tracks and registers them on
// the video record. The admin only submits the paste -- nothing here uploads
// or registers a track itself.
//
// The paste does not have to come from the clipboard. A video that already has
// its English track carries the source text at a public URL, so filling in a
// language that failed or was never generated is a matter of reading that track
// back rather than finding the original transcription again.

const LANGUAGE_LABELS = SUBTITLE_LABELS;

const EMPTY_PIPELINE = {
  open: false,
  vtt: '',
  loading: false,
  error: '',
  gate: null,
  result: null,
  // 'paste' was typed in by hand and must never be thrown away silently;
  // 'stored' was read back from the record and can always be read again.
  source: 'paste',
  sourcing: false,
  // What was missing when the dialog opened. Held here rather than recomputed
  // from the record, because a successful run fills the gaps -- and the dialog
  // is still on screen, still explaining the job it just did.
  gaps: { missing: [], present: [] },
};

// Shared by the pre-flight gate and the success view -- both carry the same
// { changed, material, details } shape.
function CorrectionsDiff({ corrections }) {
  const details = corrections?.details || [];
  if (!details.length) return null;

  return (
    <Accordion sx={{ mt: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2">
          {corrections.changed} cue{corrections.changed === 1 ? '' : 's'} corrected
          {typeof corrections.material === 'number' ? `, ${corrections.material} material` : ''}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ maxHeight: 320, overflowY: 'auto' }}>
        {details.map((d, i) => (
          <Box key={d.id ?? i} sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary">cue {d.id}</Typography>
            <Typography variant="body2" sx={{ color: 'error.main', textDecoration: 'line-through' }}>
              {d.before}
            </Typography>
            <Typography variant="body2" sx={{ color: 'success.main' }}>{d.after}</Typography>
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
}

function SubtitleManager({ videoData, videoIdentifier, onUpdateVideoData, onNotify = () => {} }) {
  const [pipeline, setPipeline] = useState(EMPTY_PIPELINE);
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const handleDeleteSubtitle = async (index) => {
    const updatedSubtitles = [...(videoData.subtitles || [])];
    if (index >= updatedSubtitles.length) {
      onNotify('Subtitle not found.', 'error');
      return;
    }
    const [removed] = updatedSubtitles.splice(index, 1);
    setConfirmDelete(null);
    try {
      onUpdateVideoData(await patchVideo(videoIdentifier, { subtitles: updatedSubtitles }));
      onNotify(`Removed the ${removed.label || removed.srclang} track.`, 'success');
    } catch (error) {
      onNotify('Failed to update subtitles.', 'error');
    }
  };

  const stale = staleFlags(videoData);
  const coverage = subtitleCoverage(videoData);
  // Only worth offering while there is a source to derive from and something
  // to derive: with no English track the pipeline has no input, and with all
  // five present the regular regenerate button already says what it does.
  const canFillGaps = !!coverage.source && coverage.missing.length > 0;

  const refreshVideoData = async () => {
    try {
      // Fresh tracks are timed against the current file, so this half of the
      // staleness is resolved -- the chapters' half is not.
      if (stale.subtitles) {
        await patchVideo(videoIdentifier, stalePatch({ ...stale, subtitles: false })).catch(
          () => null,
        );
      }
      onUpdateVideoData(await getVideo(videoIdentifier));
    } catch (error) {
      onNotify('Subtitles were registered, but the view could not refresh.', 'warning');
    }
  };

  // `force` re-submits the identical paste past a 422 gate the admin has read
  // and accepted.
  const runPipeline = async (force = false) => {
    const vtt = pipeline.vtt;

    if (!vtt.trim() || !(vtt.includes('WEBVTT') || vtt.includes('-->'))) {
      setPipeline((prev) => ({ ...prev, error: 'That does not look like VTT content.', gate: null }));
      return;
    }

    setPipeline((prev) => ({ ...prev, loading: true, error: '', gate: null, result: null }));

    try {
      // Correction plus four translations runs 30s to ~2 minutes, and 422 is a
      // gate to read rather than an error -- both handled inside the client.
      const { status, data } = await subtitlePipeline({ identifier: videoIdentifier, vtt, force });

      if (status === 200) {
        setPipeline((prev) => ({ ...prev, loading: false, result: data }));
        await refreshVideoData();
        const failed = Object.keys(data?.errors || {});
        onNotify(
          failed.length
            ? `Registered ${Object.keys(data?.tracks || {}).length} track(s); ${failed.join(', ')} failed.`
            : 'All five subtitle tracks registered.',
          failed.length ? 'warning' : 'success',
        );
        return;
      }

      if (status === 422) {
        // Two distinct gates share this status: a paste that does not look
        // English, and a correction pass that wanted to rewrite too much.
        const payload = unwrap(data);
        const isLanguageGate = !!payload?.detected_language && !payload?.material_cue_ids;
        setPipeline((prev) => ({
          ...prev,
          loading: false,
          gate: {
            kind: isLanguageGate ? 'language' : 'corrections',
            message: messageOf(data),
            detectedLanguage: payload?.detected_language,
            corrections: payload?.corrections,
            materialCueIds: payload?.material_cue_ids,
          },
        }));
        return;
      }

      const message =
        status === 401
          ? 'Wrong password — nothing was uploaded.'
          : status === 502
            ? `The pipeline failed: ${Object.entries(unwrap(data)?.errors || {}).map(([l, m]) => `${l}: ${m}`).join('; ') || 'unknown error'}`
            : messageOf(data) || `The pipeline returned ${status}.`;

      setPipeline((prev) => ({ ...prev, loading: false, error: message }));
    } catch (error) {
      setPipeline((prev) => ({ ...prev, loading: false, error: `Could not reach the pipeline: ${error.message}` }));
    }
  };

  // Open the same dialog with the English track already in it. The pipeline
  // takes one input -- an English VTT -- and the video is carrying one, so the
  // missing languages do not need the original transcription to be found again.
  const openFromStoredEnglish = async () => {
    setPipeline({
      ...EMPTY_PIPELINE,
      open: true,
      source: 'stored',
      sourcing: true,
      gaps: { missing: coverage.missing, present: coverage.present },
    });
    try {
      const vtt = await fetchSubtitleText(coverage.source.src);
      if (!vtt.includes('WEBVTT') && !vtt.includes('-->')) {
        setPipeline((prev) => ({
          ...prev,
          sourcing: false,
          error: 'The stored English track did not come back as VTT. Paste the transcription instead.',
        }));
        return;
      }
      setPipeline((prev) => ({ ...prev, sourcing: false, vtt }));
    } catch (error) {
      setPipeline((prev) => ({
        ...prev,
        sourcing: false,
        error: `Could not read the stored English track: ${error.message}`,
      }));
    }
  };

  const requestClose = () => {
    if (pipeline.loading) return;
    // A paste is long and hand-assembled; never discard it silently. Text read
    // back from the record costs one fetch to get again, so it just closes.
    if (pipeline.source === 'paste' && pipeline.vtt.trim() && !pipeline.result) {
      setConfirmClose(true);
      return;
    }
    setPipeline(EMPTY_PIPELINE);
  };

  const tracks = pipeline.result?.tracks || {};
  const trackLangs = Object.keys(tracks);
  const resultErrors = Object.entries(pipeline.result?.errors || {});

  return (
    <Box>
      {stale.subtitles && (
        <Alert severity="warning" sx={{ mb: '10px' }}>
          The video file was replaced with a different timeline — these subtitle tracks
          are probably out of date. Regenerating them clears this warning.
        </Alert>
      )}

      {videoData.subtitles && videoData.subtitles.length > 0 ? (
        videoData.subtitles.map((subtitle, index) => (
          <Box
            key={`${subtitle.srclang}-${index}`}
            sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '4px' }}
          >
            <span className="admin-label" style={{ minWidth: 28 }}>{subtitle.srclang}</span>
            <Typography variant="body2" sx={{ flex: 1 }}>{subtitle.label}</Typography>
            {subtitle.src && (
              <Link href={subtitle.src} target="_blank" rel="noopener" sx={{ fontSize: 11 }}>
                open
              </Link>
            )}
            {/* Quiet until hover, and armed by a confirm: dropping a track
                throws away one fifth of a pipeline run. */}
            <IconButton
              size="small"
              onClick={() => setConfirmDelete(index)}
              aria-label={`Delete ${subtitle.label} track`}
              sx={{ '&:hover': { color: 'var(--ps-mute)' } }}
            >
              <DeleteIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>
        ))
      ) : (
        <Typography variant="body2" sx={{ color: 'var(--ps-text-dim)' }}>
          No subtitles yet. Without an English track there is no video chat either.
        </Typography>
      )}

      {/* A gap is an instruction, not a hole: name the languages that are
          missing and put the thing that fills them right underneath. */}
      {canFillGaps && (
        <Box sx={{ mt: '18px' }}>
          <span className="admin-label">
            {coverage.missing.length} track{coverage.missing.length === 1 ? '' : 's'} missing
          </span>
          <Typography variant="body2" sx={{ color: 'var(--ps-text-dim)', mt: '4px' }}>
            No {coverage.missing.map((lang) => LANGUAGE_LABELS[lang] || lang).join(', ')} track
            {coverage.missing.length === 1 ? '' : 's'}. They can be translated from the English
            one this video already has — you do not need the original transcription.
          </Typography>
          <Box sx={{ mt: '10px' }}>
            <Button
              variant="contained"
              startIcon={<TranslateIcon sx={{ fontSize: 15 }} />}
              onClick={openFromStoredEnglish}
            >
              Generate the missing track{coverage.missing.length === 1 ? '' : 's'}
            </Button>
          </Box>
        </Box>
      )}

      <Box sx={{ mt: '18px' }}>
        <Button
          startIcon={<AutoFixHighIcon sx={{ fontSize: 15 }} />}
          onClick={() => setPipeline({ ...EMPTY_PIPELINE, open: true })}
        >
          Add / regenerate subtitles
        </Button>
      </Box>

      <Dialog open={pipeline.open} onClose={requestClose} fullWidth maxWidth="md">
        <DialogTitle>
          {pipeline.source === 'stored'
            ? `Generate the missing track${pipeline.gaps.missing.length === 1 ? '' : 's'}`
            : 'Add / regenerate subtitles'}
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText component="div">
            {pipeline.source === 'stored' ? (
              <>
                This is the English track already registered on the video, read back from
                storage. The pipeline corrects it, translates it to German, French, Italian
                and Spanish, and registers all five tracks.
              </>
            ) : (
              <>
                Paste the English auto-transcription VTT below. The pipeline corrects the
                transcription, translates it to German, French, Italian and Spanish, and
                registers all five tracks.
              </>
            )}
          </DialogContentText>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <AlertTitle>This replaces all five subtitle tracks</AlertTitle>
            {pipeline.source === 'stored' ? (
              <>
                There is no way to generate only{' '}
                {pipeline.gaps.missing.map((l) => LANGUAGE_LABELS[l] || l).join(' and ')} — the
                pipeline runs whole. The {pipeline.gaps.present.length} track
                {pipeline.gaps.present.length === 1 ? '' : 's'} this video already has will be
                regenerated and overwritten along with the missing one
                {pipeline.gaps.missing.length === 1 ? '' : 's'}.
              </>
            ) : (
              <>
                Including English — the track stored is the pipeline's corrected version of
                what you paste, not the paste itself. Any existing tracks for this video are
                overwritten.
              </>
            )}
          </Alert>

          <TextField
            fullWidth
            multiline
            rows={12}
            margin="normal"
            label="English VTT"
            placeholder="WEBVTT&#10;&#10;1&#10;00:00:00.000 --> 00:00:03.000&#10;..."
            value={pipeline.vtt}
            onChange={(e) => setPipeline((prev) => ({ ...prev, vtt: e.target.value, source: 'paste' }))}
            disabled={pipeline.loading || pipeline.sourcing}
          />

          {pipeline.sourcing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Reading the registered English track…
              </Typography>
            </Box>
          )}

          {pipeline.loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Correcting and translating — this takes 30 seconds to about two minutes.
              </Typography>
            </Box>
          )}

          {pipeline.error && <Alert severity="error" sx={{ mt: 2 }}>{pipeline.error}</Alert>}

          {pipeline.gate?.kind === 'language' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <AlertTitle>This does not look like English</AlertTitle>
              {pipeline.gate.message || `The paste was detected as "${pipeline.gate.detectedLanguage}".`}
              {' '}Submitting anyway will treat it as the English source track.
              <Box sx={{ mt: 1 }}>
                <Button size="small" variant="contained" color="warning" onClick={() => runPipeline(true)}>
                  Use it anyway
                </Button>
              </Box>
            </Alert>
          )}

          {pipeline.gate?.kind === 'corrections' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <AlertTitle>The correction step wants to change a lot</AlertTitle>
              {pipeline.gate.message
                || `${pipeline.gate.materialCueIds?.length || 0} cues would be materially rewritten.`}
              {' '}Review the changes below before accepting.
              <CorrectionsDiff corrections={pipeline.gate.corrections} />
              <Box sx={{ mt: 1 }}>
                <Button size="small" variant="contained" color="warning" onClick={() => runPipeline(true)}>
                  Accept anyway
                </Button>
              </Box>
            </Alert>
          )}

          {pipeline.result && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Alert severity={!trackLangs.length ? 'error' : resultErrors.length ? 'warning' : 'success'}>
                <AlertTitle>
                  {!trackLangs.length
                    ? 'Nothing was registered'
                    : resultErrors.length
                      ? 'Registered with some failures'
                      : 'All tracks registered'}
                </AlertTitle>
                {pipeline.result.source_cues} source cues
                {pipeline.result.detected_language ? `, detected as ${pipeline.result.detected_language}` : ''}.
                {!trackLangs.length && resultErrors.length ? (
                  // Correction and translation ran but every registration was
                  // rejected -- the usual cause is a password that authenticates
                  // with the pipeline but not with addsubtitle.
                  <Box sx={{ mt: 1 }}>
                    The transcript was corrected and translated, but no track could be
                    registered. If every language failed on authorization, the password
                    in use is not the learn admin password.
                  </Box>
                ) : null}
              </Alert>

              <Box sx={{ mt: 2 }}>
                {trackLangs.map((lang) => (
                  <Box key={lang} sx={{ display: 'flex', gap: 1, alignItems: 'baseline', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ minWidth: 90 }}>
                      {tracks[lang].label || LANGUAGE_LABELS[lang] || lang}
                    </Typography>
                    <Link href={tracks[lang].src} target="_blank" rel="noopener" variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {tracks[lang].src}
                    </Link>
                  </Box>
                ))}
              </Box>

              {resultErrors.map(([lang, message]) => (
                <Alert severity="error" key={lang} sx={{ mt: 1 }}>
                  {LANGUAGE_LABELS[lang] || lang}: {message} — resubmitting the same paste is safe
                  and will retry the missing languages.
                </Alert>
              ))}

              {pipeline.result.corrections && <CorrectionsDiff corrections={pipeline.result.corrections} />}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={requestClose} disabled={pipeline.loading}>
            {pipeline.result ? 'Done' : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            onClick={() => runPipeline(false)}
            disabled={pipeline.loading || pipeline.sourcing || !pipeline.vtt.trim()}
          >
            {pipeline.loading ? 'Working…' : 'Generate all subtitles'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)}>
        <DialogTitle>Discard the pasted VTT?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Closing now discards what you pasted. Nothing has been uploaded.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)}>Keep editing</Button>
          <Button
            color="error"
            onClick={() => {
              setConfirmClose(false);
              setPipeline(EMPTY_PIPELINE);
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDelete !== null} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete subtitle track</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            Remove the {videoData.subtitles?.[confirmDelete]?.label || ''} track? Regenerating it
            means another full pipeline run.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => handleDeleteSubtitle(confirmDelete)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SubtitleManager;
